/* oxlint-disable typescript/no-unsafe-assignment -- interfacing with untyped Ember models during migration */
/* oxlint-disable typescript/no-unsafe-member-access -- interfacing with untyped Ember models during migration */
import { useToast } from '@repo/design-system-kit/components/toast/provider/use-toast';
import { useFlags } from '@repo/poly-hooks/flags';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import {
  CompanyActivityForm,
  type CompanyActivityFormValues,
} from 'register-partners-js/react/components/company-activity-form/company-activity-form';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import styles from '../business-activity/business-activity.module.css';
import { useEmberRegistration } from '../../hooks/use-ember-registration';

export function CompanyActivity(): React.JSX.Element | null {
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  const { registration, isLoading, isError } = useEmberRegistration(registrationId);
  const { formatMessage } = useIntl();
  const { showToast } = useToast();
  const flags = useFlags();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const organization = registration?.belongsTo('organization').value();
  const isFrenchNewActivityScreenEnabled =
    organization?.get('legalCountry') === 'FR' && flags.featureBooleanFrNewActivityScreen;

  useEffect(() => {
    if (isLoading || isError || !registration || !organization) {
      return;
    }

    if (!isFrenchNewActivityScreenEnabled) {
      navigate(`/registrations/${registrationId}/business-finances`, { replace: true });
    }
  }, [
    isError,
    isFrenchNewActivityScreenEnabled,
    isLoading,
    navigate,
    organization,
    registration,
    registrationId,
  ]);

  function handleGoBack(): void {
    navigate(`/registrations/${registrationId}/business-activity`, { replace: true });
  }

  async function handleSubmit(values: CompanyActivityFormValues): Promise<void> {
    if (!organization) {
      return;
    }

    setIsSubmitting(true);

    try {
      organization.set('businessRelationships', values.businessRelationships);
      organization.set('businessRelationshipsOther', values.businessRelationshipsOther);
      organization.set('incomingTransfersOrigin', values.incomingTransfersOrigin);
      organization.set('outgoingTransfersDestination', values.outgoingTransfersDestination);
      await organization.save();

      navigate(`/registrations/${registrationId}/business-finances`, { replace: true });
    } catch {
      showToast({
        type: 'error',
        text: formatMessage({ id: 'errors.internal-server-error' }),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return null;
  }

  if (isError || !registration) {
    return (
      <MainLayout>
        <div className={styles.layout}>
          <div className={styles.content}>
            {formatMessage({ id: 'errors.internal-server-error' })}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!organization || !isFrenchNewActivityScreenEnabled) {
    return null;
  }

  return (
    <MainLayout>
      <div className={styles.layout}>
        <div className={styles.content}>
          <CompanyActivityForm
            goBack={handleGoBack}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            organization={organization}
          />
        </div>
      </div>
    </MainLayout>
  );
}
